import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "../services/api";
import Card from "../components/Card";
import Button from "../components/Button";
import Modal from "../components/Modal";
import Input from "../components/Input";
import { toast } from "react-toastify";
import {
  ArrowRightIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

/* ---------------- AddStudentsModal (uses advanced search) ---------------- */
const AddStudentsModal = ({ onSave, onCancel, existingSerials = [] }) => {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const debTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (debTimeout.current) clearTimeout(debTimeout.current);
    };
  }, []);

  const handleSearchChange = (val) => {
    setQuery(val);
    setError("");
    setSearchResults([]);

    if (debTimeout.current) clearTimeout(debTimeout.current);
    if (!val || val.trim().length < 1) {
      return;
    }

    debTimeout.current = setTimeout(async () => {
      try {
        setLoadingSearch(true);
        const res = await api.searchStudents(val.trim());
        const students = res.data || res;
        setSearchResults(students);
      } catch (err) {
        setError(err.response?.data?.message || "فشل في البحث");
      } finally {
        setLoadingSearch(false);
      }
    }, 300);
  };

  const addToSelection = (student) => {
    if (selectedStudents.some((s) => s.serialNumber === student.serialNumber)) {
      setError("تم اختيار هذا الطالب بالفعل");
      return;
    }
    if (existingSerials.includes(student.serialNumber)) {
      setError("الطالب موجود بالفعل في هذه الشعبة");
      return;
    }
    setSelectedStudents((p) => [...p, student]);
    setError("");
  };

  const removeStudent = (serial) => {
    setSelectedStudents(selectedStudents.filter((s) => s.serialNumber !== serial));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudents.length) {
      setError("أضف طالبًا واحدًا على الأقل");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        studentsToAdd: selectedStudents.map((s) => ({
          serialNumber: s.serialNumber,
          previousGrade: s.previousGrade || "",
          gradeOfThisYear: s.gradeOfThisYear || "",
        })),
      };
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.message || "فشل إضافة الطلاب");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-md mb-1">بحث متقدم عن طلاب (الاسم أو الرقم التسلسلي)</label>

        {/* ---------- UPDATED SEARCH ROW: allow wider input ---------- */}
        <div className="flex gap-2 w-full items-start">
          <div className="flex-1 min-w-0">
            <Input
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="اكتب اسم الطالب أو الرقم التسلسلي..."
              className="w-full"
              style={{ minWidth: 520 }} // <-- temporary test width to ensure expansion; remove if desired
            />
          </div>

          <div className="shrink-0">
            <Button
              type="button"
              onClick={() => handleSearchChange(query)}
              disabled={loadingSearch}
              className="bg-green-600 hover:bg-green-700"
            >
              {loadingSearch ? "جاري البحث..." : "بحث"}
            </Button>
          </div>
        </div>
        {/* ---------- end updated search row ---------- */}

        {error && <p className="text-red-500 mt-2">{error}</p>}

        {searchResults && searchResults.length > 0 && (
          <div className="mt-3 border rounded p-2 max-h-48 overflow-auto bg-white">
            {searchResults.map((s) => {
              const alreadySelected = selectedStudents.some((ss) => ss.serialNumber === s.serialNumber);
              const alreadyInClass = existingSerials.includes(s.serialNumber);
              return (
                <div key={s.serialNumber} className="flex items-center justify-between p-2 hover:bg-gray-50">
                  <div>
                    <div className="font-medium">
                      {s.FirstName ? `${s.FirstName} ${s.FatherName || ""} ${s.LastName || ""}` : s.name || "N/A"}
                    </div>
                    <div className="text-xs text-gray-600">#{s.serialNumber} {s.MotherName ? `- ${s.MotherName}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {alreadyInClass ? (
                      <span className="text-sm text-yellow-600">موجود في الشعبة</span>
                    ) : alreadySelected ? (
                      <span className="text-sm text-green-600">مُختار</span>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => addToSelection(s)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        إضافة
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedStudents.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">✅ الطلاب المختارون:</h4>
          {selectedStudents.map((s) => (
            <div key={s.serialNumber} className="flex justify-between items-center bg-gray-100 p-2 rounded">
              <span>
                {s.FirstName ? `${s.FirstName} ${s.FatherName || ""} ${s.LastName || ""}` : s.name || "N/A"} ({s.serialNumber})
              </span>
              <button
                type="button"
                onClick={() => removeStudent(s.serialNumber)}
                className="text-red-600 hover:text-red-800"
              >
                ❌ إزالة
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          <XMarkIcon className="h-4 w-4 ml-1" /> إلغاء
        </Button>
        <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
          {submitting ? "جارٍ الحفظ..." : "إضافة"}
        </Button>
      </div>
    </form>
  );
};

/* ---------------- ClassDetailPage ---------------- */
const ClassDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [classGroup, setClassGroup] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const res = await api.getClassGroupById(id);
      setClassGroup(res.data);
      setStudents(res.data.students || []);
    } catch (err) {
      toast.error("فشل في جلب تفاصيل الشعبة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const handleStudentChange = (i, field, value) => {
    const copy = [...students];
    copy[i][field] = value;
    setStudents(copy);
  };

  const saveChanges = async () => {
    try {
      const payload = {
        students: students.map((s) => ({
          serialNumber: s.serialNumber,
          previousGrade: s.previousGrade,
          gradeOfThisYear: s.gradeOfThisYear,
        })),
      };
      await api.updateClassGroup(id, payload);
      toast.success("تم حفظ التعديلات بنجاح");
      setEditMode(false);
      fetchDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل في حفظ التعديلات");
    }
  };

  const removeStudent = async (serialNumber) => {
    if (!window.confirm("هل تريد حذف هذا الطالب؟")) return;
    try {
      await api.updateClassGroup(id, { studentsToRemove: [serialNumber] });
      toast.success("تم حذف الطالب");
      fetchDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل في حذف الطالب");
    }
  };
const addStudents = async (payload) => {
  try {
    const res = await api.updateClassGroup(id, payload);
    console.log("addStudents: backend response:", res);

    const results = res?.data?.addResults;

    if (Array.isArray(results)) {
      let hasSuccess = false;

      // Fire toasts for every item in addResults
      results.forEach((r) => {
        const msg = r.message || r.detail || r.error || JSON.stringify(r);

        // Map statuses to toast types (adjust statuses if backend uses others)
        if (r.status === "added" || r.status === "success") {
          toast.success(msg);
          hasSuccess = true;
        } else if (r.status === "exists-in-other-class" || r.status === "exists-in-another-class" || r.status === "conflict") {
          // show backend message as an error/warning
          toast.error(msg);
        } else if (r.status === "already-in-this-class") {
          toast.error(msg);
        } else {
          // fallback to info-style toast
          toast(msg);
        }
      });

      // delay UI changes slightly so toasts have time to render
      setTimeout(async () => {
        if (hasSuccess) setIsAddModalOpen(false);
        await fetchDetails();
      }, 120);

      return;
    }

    // Fallback: top-level message
    if (res?.data?.message) {
      toast.error(res.data.message);
      // don't close modal unless explicit success
      if (res.data.status === "success" || res.data.status === "added") {
        setTimeout(() => {
          setIsAddModalOpen(false);
          fetchDetails();
        }, 120);
      }
      return;
    }

    // Generic fallback success
    toast.success("تمت العملية");
    setTimeout(() => {
      setIsAddModalOpen(false);
      fetchDetails();
    }, 120);
  } catch (err) {
    console.error("addStudents error:", err);
    const serverMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.response?.data?.detail ||
      err.message ||
      "فشل في إضافة الطلاب";
    toast.error(serverMessage);
  }
};


  const deleteClassGroup = async () => {
    if (!window.confirm("هل أنت متأكد أنك تريد حذف هذه الشعبة؟")) return;
    try {
      await api.deleteClassGroup(id);
      toast.success("تم حذف الشعبة بنجاح");
      navigate("/classes");
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل في حذف الشعبة");
    }
  };

  if (loading) return <p className="p-4">جاري التحميل...</p>;
  if (!classGroup) return <p className="p-4 text-red-500">لم يتم العثور على الشعبة</p>;

  const existingSerials = (students || []).map((s) => s.serialNumber);

  return (
    <Card title={`📘 تفاصيل الشعبة: ${classGroup.className} - ${classGroup.roomNumber}`}>
      <div className="flex justify-between mb-4">
        <Button variant="secondary" onClick={() => navigate(-1)} className="flex items-center">
          <ArrowRightIcon className="h-5 w-5 ml-1" /> رجوع
        </Button>

        <div className="flex gap-2">
          {!editMode ? (
            <Button
              onClick={() => setEditMode(true)}
              className="bg-blue-600 hover:bg-blue-700 flex items-center"
            >
              <PencilSquareIcon className="h-5 w-5 ml-2" /> تعديل الطلاب
            </Button>
          ) : (
            <>
              <Button onClick={saveChanges} className="bg-green-600 hover:bg-green-700 flex items-center">
                <CheckIcon className="h-5 w-5 ml-2" /> حفظ
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditMode(false);
                  fetchDetails();
                }}
                className="flex items-center"
              >
                <XMarkIcon className="h-5 w-5 ml-2" /> إلغاء
              </Button>
            </>
          )}

          <Button onClick={() => setIsAddModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 flex items-center">
            <UserPlusIcon className="h-5 w-5 ml-2" /> إضافة طلاب جدد
          </Button>

          <Button onClick={deleteClassGroup} className="bg-red-600 hover:bg-red-700 flex items-center">
            <TrashIcon className="h-5 w-5 ml-2" /> حذف الشعبة
          </Button>
        </div>
      </div>

      <p className="text-slate-900 mb-2">
        العام الدراسي: <span className="font-semibold">{classGroup.academicYear}</span>
      </p>
      <p className="text-slate-900 mb-4">
        عدد الطلاب: <span className="font-semibold">{students.length}</span>
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full border text-md align-middle">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border ">#</th>
              <th className="p-2 border">اسم الطالب الكامل </th>
              <th className="p-2 border">الرقم في السجل العام </th>
              <th className="p-2 border">النتيجة السابقة</th>
              <th className="p-2 border">نتيجة آخر العام</th>
              <th className="p-2 border">إجراءات</th>
            </tr>
          </thead>
            <tbody>
              {students.map((s, i) => (
                <tr
                  key={s.serialNumber || i}
                  className=" font-medium text-md"
                >
                  <td className="p-2 border text-center">{i + 1}</td>

                  <td className="p-2 border text-center">
                    {s.student?.FirstName
                      ? `${s.student.FirstName} ${s.student.FatherName} ${s.student.LastName}`
                      : "N/A"}
                  </td>

                  <td className="p-2 border text-center">{s.serialNumber}</td>

                  <td className="p-2 border text-center">
                    {editMode ? (
                      <Input
                        value={s.previousGrade || ""}
                        onChange={(e) => handleStudentChange(i, "previousGrade", e.target.value)}
                        className="w-28"
                      />
                    ) : (
                      s.previousGrade || "-"
                    )}
                  </td>

                  <td className="p-2 border text-center">
                    {editMode ? (
                      <Input
                        value={s.gradeOfThisYear || ""}
                        onChange={(e) => handleStudentChange(i, "gradeOfThisYear", e.target.value)}
                        className="w-28"
                      />
                    ) : (
                      s.gradeOfThisYear || "-"
                    )}
                  </td>

                  <td className="p-2 border text-center align-middle">
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeStudent(s.serialNumber);
                      }}
                      className="text-red-500 hover:text-red-700 mx-auto block"
                    >
                      <TrashIcon className="h-6 w-6" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>


        </table>
      </div>

      {/* ---------- UPDATED: wrap modal content to allow wider input ---------- */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="إضافة طلاب للشعبة">
        <div className="w-full max-w-4xl mx-auto p-4">
          <AddStudentsModal
            onSave={addStudents}
            onCancel={() => setIsAddModalOpen(false)}
            existingSerials={existingSerials}
          />
        </div>
      </Modal>
      {/* ---------- end modal update ---------- */}
    </Card>
  );
};

export default ClassDetailPage;
